import { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { MdOutlineEdit, MdDeleteOutline } from "react-icons/md";
import { Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid2, IconButton, InputAdornment, InputLabel, MenuItem, Paper, Select, TextField, Typography } from '@mui/material';
import SidebarNav from './SidebarNav';
import { useAppDispatch, useAppSelector } from '../hooks/redux-hooks';
import { useEffect } from 'react';
import { Add, Search } from '@mui/icons-material';
import { createAttachment, createNewMachine, deleteMachine, fetchBoughtOutList, fetchMachineAttachmentLinks, fetchMachineList } from '../slices/machineSlice';
import { nav_boughtouts, nav_machines, TableRowStyled } from '../constants';
import RefreshButton from '../components/RefreshButton';
import { useLocation, useNavigate } from 'react-router-dom';
import { MdOutlineRemoveRedEye } from "react-icons/md";
import DisplaySnackbar from '../utils/DisplaySnackbar';
import { useSnackbar } from 'notistack';


export default function Machines() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { state } = useLocation()
  const { enqueueSnackbar } = useSnackbar()

  const { machines, machineStatus } = useAppSelector(
    (state) => state.machine
  );

  const [searchText, setSearchText] = useState("")
  const [errors, setErrors] = useState<any>();
  const [createDialog, setCreateDialog] = useState(false)
  const [machineId, setMachineId] = useState("")
  const [deleteDialog, setDeleteDialog] = useState({ dialog: false, id: '', name: '' })
  const [formData, setFormData] = useState({
    model_no: '',
    machine_name: '',
    spindles: 0,
    side_type: '',
    max_spindles: 0,
    min_spindles: 0
  });

  const [images, setImages] = useState<any>([]);
  const [urls, setUrls] = useState([""]);

  useEffect(() => {
    dispatch(fetchMachineList()).unwrap()
  }, [])

  useEffect(() => {
    if (state?.openCreate) {
      setErrors({})
      setCreateDialog(true)
      navigate('.', { replace: true, state: null })
    }
  }, [state])

  const handleSearch = () => {
    dispatch(fetchMachineList(searchText)).unwrap()
  }

  const handleRefresh = () => {
    dispatch(fetchMachineList(searchText)).unwrap()
  }

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e: any) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png"];

    if (!allowedTypes.includes(file.type)) {
      alert("Only JPG and PNG images are allowed");
      return;
    }

    if (images.length >= 3) return;

    const preview = URL.createObjectURL(file);
    const name = file.name;

    setImages((prev: any) => [...prev, { type: 'new', file, preview, name }]);
  };

  const handleRemoveImage = (index: any) => {
    const updated = [...images];
    updated.splice(index, 1);
    setImages(updated);
  };

  const handleUrlChange = (index: any, value: any) => {
    const updated = [...urls];
    updated[index] = value;
    setUrls(updated);
  };

  const addUrlField = () => {
    if (urls.length < 3) {
      setUrls([...urls, ""]);
    }
  };

  const validate = () => {
    const newErrors: any = {};

    if (!formData.model_no) newErrors.model_no = 'Model no is required';
    if (!formData.machine_name) newErrors.machine_name = 'Machine name is required';
    if (!formData.side_type) newErrors.side_type = 'Side type is required';
    if (!formData.spindles || formData.spindles == 0) newErrors.spindles = 'Spindles is required';

    return newErrors;
  };

  const clearValues = () => {
    setFormData({
      model_no: '',
      machine_name: '',
      spindles: 0,
      side_type: '',
      max_spindles: 0,
      min_spindles: 0
    })
    setErrors({})
  }

  const handleNewMachineSubmit = () => {
     const validated = validate()
     if(Object.keys(validated).length == 0){
      dispatch(createNewMachine({
        type: machineId.length == 0 ? 'Add' : 'Edit',
        id: machineId,
        model_no: formData.model_no,
        machine_name: formData.machine_name,
        side_type: formData.side_type,
        spindles: formData.spindles,
        min_spindles: formData.min_spindles,
        max_spindles: formData.max_spindles,
        video_urls: urls
      })).unwrap().then((res) => {
        DisplaySnackbar(res, res.message.includes('success') ? "success" : "error", enqueueSnackbar)
        console.log("--------", res)
        if (images?.filter((img:any) => img.type == 'new')?.length > 0) {
          uploadAttachments(res.id)
        }
        if (res.message.includes('success')) {
          setCreateDialog(false)
          clearValues()
          dispatch(fetchMachineList())
        }
      }).catch((err) => {
        DisplaySnackbar(err.message, 'error', enqueueSnackbar)
      })
     }else{
       setErrors(validated)
     }
  }

  const uploadAttachments = (id: string) => {
    const machineImages = images.filter((img:any) => img.type == 'new')?.map((img:any) => img.file);
    dispatch(createAttachment({
      files: machineImages, type: 'machine',
      type_id: id
    })).unwrap()
      .then((res: any) => {
        DisplaySnackbar(res, res.includes('success') ? "success" : "error", enqueueSnackbar)
        navigate(-1)
      })
      .catch((err: any) => {
        DisplaySnackbar(err.message, 'error', enqueueSnackbar)
        navigate(-1)
      })
  }

  return (
    <Box sx={{ display: 'flex', direction: 'column' }}>
      <SidebarNav currentPage={nav_machines} />

      <Grid2 container spacing={2} padding={2} sx={{ mt: 10, flexGrow: 1 }}>
        <Grid2 size={{ xs: 6, md: 8 }}>
          <TextField
            placeholder='Search machines'
            variant="outlined"
            size='small'
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value)
            }}
            onKeyDown={(ev) => {
              if (ev.key == "Enter") {
                handleSearch()
              }
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid2>
        <Grid2 size="grow" display="flex" alignItems="end" flexDirection="column">
          <Box sx={{ display: 'flex', gap: 1 }}>
          <RefreshButton onClick={handleRefresh} />
          <Button variant="contained" startIcon={<Add />} size="small" onClick={() => {
            // navigate("/machines/newMachine")
            setErrors({})
            setCreateDialog(true)
          }}>
            Add New
          </Button>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 6, md: 12 }}>
          <TableContainer component={Paper}>
            <Table sx={{ '& .MuiTableCell-head': { lineHeight: 0.8, backgroundColor: "#fadbda", fontWeight:'bold' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Modle No</TableCell>
                  <TableCell>Machine Name</TableCell>
                  <TableCell>Side Type</TableCell>
                  <TableCell>Spindles</TableCell>
                  <TableCell>Max/ Min Spindles</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {machines.length > 0 ? machines.map((row) => (
                  <TableRowStyled key={row.id}>
                    <TableCell>{row.model_no}</TableCell>
                    <TableCell>{row.machine_name}</TableCell>
                    <TableCell>{row.side_type}</TableCell>
                    <TableCell>{row.spindles}</TableCell>
                    <TableCell>{row.max_spindles} / {row.min_spindles}</TableCell>
                    <TableCell><MdOutlineEdit style={{ cursor: 'pointer' }} onClick={()=>{
                      setCreateDialog(true)
                      setMachineId(row.id)
                      dispatch(fetchMachineAttachmentLinks(row.id)).unwrap().then((res:any) => {
                        const videoLinks = res?.links?.filter((link: string) => link.includes("youtube.com") || link.includes("youtu.be"));
                        setUrls(videoLinks);
                        const imageUrls: any = [];
                        res?.links?.filter((link: string) => !link.includes("youtube.com") && !link.includes("youtu.be"))?.map((link: any) => {
                          imageUrls.push({
                            type: 'existing',
                            url: link
                          })
                        });
                        setImages(imageUrls);
                      })
                      setFormData({
                        model_no: row.model_no,
                        machine_name: row.machine_name,
                        side_type: row.side_type,
                        spindles: row.spindles,
                        min_spindles: row.min_spindles,
                        max_spindles: row.max_spindles
                      })
                    }} /></TableCell>
                    <TableCell style={{ cursor: 'pointer' }} onClick={() => {
                      navigate("/machines/newMachine", {
                        state: {
                          id: row.id
                        }
                      })
                    }}><MdOutlineRemoveRedEye /></TableCell>
                    <TableCell><MdDeleteOutline style={{ cursor: 'pointer' }} onClick={() => {
                      setDeleteDialog({ dialog: true, id: row.id, name: row.machine_name })
                    }} /></TableCell>
                  </TableRowStyled>
                )) : <TableRow key={0}>
                  <TableCell colSpan={6} align='center'>No Data</TableCell>
                </TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid2>
      </Grid2>

      <Dialog
        maxWidth={'sm'}
        open={createDialog}
        onClose={(event, reason) => {
          if (reason == "backdropClick") {
            return
          }
          setCreateDialog(false)
        }}>
        <DialogTitle>{machineId.length > 0 ? 'Update Machine' : 'New Machine'}</DialogTitle>
        <DialogContent>
          <TextField
            size='small'
            variant="outlined"
            fullWidth
            label="Model No"
            name="model_no"
            required
            sx={{mt:1}}
            value={formData.model_no}
            onChange={handleChange}
            error={!!errors?.model_no}
            helperText={errors?.model_no}
          />

          <TextField
            size='small'
            variant="outlined"
            fullWidth
            label="Machine Name"
            name="machine_name"
            required
            sx={{mt:1}}
            value={formData.machine_name}
            onChange={handleChange}
            error={!!errors?.machine_name}
            helperText={errors?.machine_name}
          />

          <FormControl fullWidth
            error={!!errors?.side_type} sx={{mt:1}}>
            <InputLabel id="role-select-label">Side Type</InputLabel>
            <Select
              size={'small'}
              labelId="role-select-label"
              id="role-select"
              label="Side Type"
              value={formData.side_type}
              onChange={(e) => {
                setFormData({ ...formData, side_type: e.target.value })
              }}
            >
              {["Single Side", "Double Side"].map((side) => {
                return <MenuItem value={side}>{side}</MenuItem>
              })}
            </Select>
          </FormControl>

          <TextField
            size='small'
            variant="outlined"
            fullWidth
            required
            type={'number'}
            label="Spindles"
            name="spindles"
            sx={{mt:1}}
            error={!!errors?.spindles}
            helperText={errors?.spindles}
            inputProps={{ min: 0 }}
            value={formData.spindles}
            onChange={(e:any)=>{
              setFormData({...formData, spindles: e.target.value, min_spindles: e.target.value, max_spindles: e.target.value})
            }}
          />

          <TextField
            size='small'
            variant="outlined"
            fullWidth
            required
            type={'number'}
            label="Min. Spindles"
            name="min_spindles"
            sx={{mt:1}}
            disabled={formData.spindles == 0}
            inputProps={{ min: formData.spindles, step: formData.spindles }}
            value={formData.min_spindles}
            onChange={handleChange}
          />

          <TextField
            size='small'
            variant="outlined"
            fullWidth
            required
            type={'number'}
            label="Max. Spindles"
            name="max_spindles"
            sx={{mt:1}}
            disabled={formData.spindles == 0}
            inputProps={{ min: formData.spindles, step: formData.spindles }}
            value={formData.max_spindles}
            onChange={handleChange}
          />

          {/* Images Section */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Upload Images (Max 3)
            </Typography>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>

              {/* Existing Images */}
              {images.map((img: any, index: any) => (
                <Box
                  key={index}
                  sx={{
                    width: 80,
                    height: 80,
                    border: "1px solid #ccc",
                    borderRadius: 2,
                    position: "relative",
                    overflow: "hidden"
                  }}
                >
                  <img
                    src={img.type === "existing" ? img.url : img.preview}
                    alt="preview"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />

                  {/* Optional Remove */}
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveImage(index)}
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      background: "rgba(0,0,0,0.5)",
                      color: "#fff"
                    }}
                  >
                    ✕
                  </IconButton>
                </Box>
              ))}

              {/* Add Button */}
              {images.length < 3 && (
                <label>
                  <input
                    type="file"
                    accept="image/png, image/jpeg"
                    hidden
                    onChange={handleImageChange}
                  />

                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      border: "2px dashed #aaa",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer"
                    }}
                  >
                    +
                  </Box>
                </label>
              )}
            </Box>
          </Box>

          {/* URLs Section */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Reference URLs (Max 3)
            </Typography>

            {urls.map((url, index) => (
              <TextField
                key={index}
                fullWidth
                size="small"
                placeholder="Enter URL"
                value={url}
                sx={{ mb: 1 }}
                onChange={(e) => handleUrlChange(index, e.target.value)}
              />
            ))}

            {urls.length < 3 && (
              <Button onClick={addUrlField} size="small">
                + Add URL
              </Button>
            )}
          </Box>

        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialog(false)
            clearValues()
          }} sx={{ color: '#bb0037' }}>Cancel</Button>
          <Button onClick={handleNewMachineSubmit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        maxWidth={'sm'}
        open={deleteDialog.dialog}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          Are you sure you want to delete {deleteDialog.name}?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialog({ dialog: false, id: '', name: '' })
          }} sx={{ color: '#bb0037' }}>No</Button>
          <Button variant="contained" size="small" onClick={() => {
            dispatch(deleteMachine({ id: deleteDialog.id })).unwrap().then((res: any) => {
              setDeleteDialog({ dialog: false, id: '', name: '' })
              DisplaySnackbar(res, res.includes('success') ? 'success' : 'error', enqueueSnackbar)
              dispatch(fetchMachineList())
            }).catch((err: any) => {
              enqueueSnackbar('Unable to delete machine', { variant: 'error' });
            })
          }}>Yes</Button>
        </DialogActions>
      </Dialog>

      <Dialog maxWidth={'md'}
        open={machineStatus == 'loading'}>
        <CircularProgress color='success' sx={{ m: 3 }} />
      </Dialog>
    </Box>
  );
}
